import { useRef, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  FileUploadOutlined as AttachFileIcon,
  Close as CloseIcon,
  Close as XIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import ImageIcon from '@icons/files/imageIcon';
import PdfIcon from '@icons/files/pdfIcon';
import ExcelIcon from '@icons/files/excelIcon';
import TextIcon from '@icons/files/textIcon';
import ZipIcon from '@icons/files/zipIcon';
import FileIcon from '@icons/files/fileIcon';
import {
  ACCEPTED_FILE_EXTENSIONS,
  BYTES_PER_MEGABYTE,
  MAX_FILE_SIZE_MB,
} from '@constants/fileTypeConstants';
import { showCustomErrorToast } from '@utils/customToast';

interface FileSelectorProps {
  selectedFiles: File[];
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  maxFileSize?: number; // in MB
  acceptedFileTypes?: string[];
  buttonText?: string;
  buttonVariant?: 'outlined' | 'contained' | 'text';
  buttonSx?: any;
  showChips?: boolean;
  maxChipsVisible?: number;
  disableSpacer?: boolean;
  iconOnly?: boolean;
  allowDeletion?: boolean;
  disabled?: boolean;
  onFileClick?: (file: File) => void;
  sourceRecId?: string;
}

export default function FileSelector({
  selectedFiles,
  onFilesChange,
  maxFiles = 10,
  maxFileSize = MAX_FILE_SIZE_MB,
  acceptedFileTypes = ACCEPTED_FILE_EXTENSIONS,
  buttonText = 'Attach Files',
  buttonVariant = 'outlined',
  buttonSx = {},
  showChips = true,
  maxChipsVisible = 3,
  disableSpacer = false,
  iconOnly = false,
  allowDeletion = true,
  disabled = false,
  onFileClick,
}: FileSelectorProps) {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showAllFiles, setShowAllFiles] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const files = event.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    const totalFiles = selectedFiles.length + newFiles.length;

    if (totalFiles > maxFiles) {
      showCustomErrorToast({ message: `Maximum ${maxFiles} files allowed` });
      return;
    }

    const maxSizeBytes = maxFileSize * BYTES_PER_MEGABYTE;
    const oversizedFiles = newFiles.filter(file => file.size > maxSizeBytes);

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => f.name).join(', ');
      showCustomErrorToast({ message: `File size exceeds ${maxFileSize}MB limit: ${fileNames}` });
      return;
    }

    const invalidFiles = newFiles.filter(file => {
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      return !acceptedFileTypes.includes(extension);
    });

    if (invalidFiles.length > 0) {
      showCustomErrorToast({
        message: `Invalid file types: ${invalidFiles.map(f => f.name).join(', ')}`
      });
      return;
    }

    const existingFileNames = selectedFiles.map(f => f.name);
    const uniqueNewFiles = newFiles.filter(
      file => !existingFileNames.includes(file.name)
    );

    onFilesChange([...selectedFiles, ...uniqueNewFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    onFilesChange(newFiles);
  };

  const handleBrowseClick = () => {
    if (disabled) return;
    if (maxChipsVisible === 0) {
      setShowAllFiles(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleFileBrowserClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const fileIconMap = useMemo(() => {
    const iconProps = { width: 20, height: 20 };
    const docIconProps = { sx: { color: '#3182ce', fontSize: 20 } };

    return {
      pdf: <PdfIcon {...iconProps} />,
      jpg: <ImageIcon {...iconProps} />,
      jpeg: <ImageIcon {...iconProps} />,
      png: <ImageIcon {...iconProps} />,
      gif: <ImageIcon {...iconProps} />,
      bmp: <ImageIcon {...iconProps} />,
      doc: <DocumentIcon {...docIconProps} />,
      docx: <DocumentIcon {...docIconProps} />,
      xls: <ExcelIcon {...iconProps} />,
      xlsx: <ExcelIcon {...iconProps} />,
      txt: <TextIcon {...iconProps} />,
      zip: <ZipIcon {...iconProps} />,
      rar: <ZipIcon {...iconProps} />,
      '7z': <ZipIcon {...iconProps} />,
    };
  }, []);

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    return (
      fileIconMap[extension as keyof typeof fileIconMap] || (
        <FileIcon width={20} height={20} />
      )
    );
  };

  const maxVisibleFiles = maxChipsVisible;
  const visibleFiles = selectedFiles.slice(0, maxVisibleFiles);
  const shouldShowViewAll =
    maxChipsVisible > 0 && selectedFiles.length > maxVisibleFiles;

  const handleFileClick = (file: File, e: React.MouseEvent) => {
    if (onFileClick && !(e.target as HTMLElement).closest('button')) {
      e.stopPropagation();
      onFileClick(file);
    }
  };

  const renderFileChip = (file: File, index: number) => {
    const isClickable = onFileClick;

    return (
      <Box
        key={`${file.name}-${index}`}
        onClick={e => isClickable && handleFileClick(file, e)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          minWidth: 'fit-content',
          maxWidth: '200px',
          flexShrink: 0,
          cursor: isClickable ? 'pointer' : 'default',
          borderRadius: 1,
          pointerEvents: isClickable ? 'auto' : 'none',
          '&:hover': isClickable
            ? {
                backgroundColor: 'action.hover',
              }
            : {},
        }}
      >
        {getFileIcon(file.name)}
        <Tooltip title={(file as any).description || file.name} arrow>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#333',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
              maxWidth: allowDeletion
                ? 'calc(200px - 60px)'
                : 'calc(200px - 40px)',
            }}
          >
            {(file as any).description || file.name}
          </Typography>
        </Tooltip>
        {allowDeletion && (
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              handleRemoveFile(index);
            }}
            sx={{
              p: 0.25,
              ml: 0.5,
              minWidth: '20px',
              width: '20px',
              height: '20px',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: '#ffebee',
              },
            }}
          >
            <XIcon sx={{ fontSize: '14px', color: '#666' }} />
          </IconButton>
        )}
      </Box>
    );
  };

  const renderFileChips = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        overflow: 'hidden',
        flex: disableSpacer ? 0 : 1,
        flexDirection: 'row-reverse',
      }}
    >
      {visibleFiles.map(renderFileChip)}
    </Box>
  );

  const renderViewAllButton = () => (
    <Typography
      variant="body2"
      sx={{
        color: '#1976d2',
        cursor: 'pointer',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        ml: 1,
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
      onClick={() => setShowAllFiles(true)}
    >
      View All ({selectedFiles.length})
    </Typography>
  );

  const renderChipsSection = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flex: disableSpacer ? 0 : 1,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {renderFileChips()}
      {shouldShowViewAll && renderViewAllButton()}
    </Box>
  );

  const renderSpacer = () => <Box sx={{ flex: 1 }} />;

  const renderAttachButton = () => {
    const shouldShowFileCount =
      maxChipsVisible === 0 && selectedFiles.length > 0;
    const displayText = shouldShowFileCount
      ? `${buttonText} (${selectedFiles.length})`
      : buttonText;

    return (
      <Button
        variant={buttonVariant}
        startIcon={!iconOnly ? <AttachFileIcon /> : undefined}
        onClick={handleBrowseClick}
        disabled={disabled}
        sx={{
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.background.paper,
          px: iconOnly ? 1 : 3,
          py: 1,
          textTransform: 'none',
          fontWeight: 500,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          minWidth: iconOnly ? 48 : 'auto',
          width: iconOnly ? 48 : 'auto',
          height: iconOnly ? 48 : 'auto',
          '&:hover': {
            borderColor: theme.palette.primary.dark,
            backgroundColor: theme.palette.primary.light + '20',
          },
          ...buttonSx,
        }}
      >
        {iconOnly ? <AttachFileIcon sx={{ fontSize: 24 }} /> : displayText}
      </Button>
    );
  };

  const renderHiddenFileInput = () => (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      onChange={handleFileSelect}
      style={{ display: 'none' }}
    />
  );

  const renderDialogTitle = () => (
    <DialogTitle
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 2,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Attachments
      </Typography>
      <IconButton onClick={() => setShowAllFiles(false)} size="small">
        <CloseIcon />
      </IconButton>
    </DialogTitle>
  );

  const renderFileListItem = (file: File, index: number) => {
    const isClickable = onFileClick;

    return (
      <ListItem
        key={`${file.name}-${index}`}
        onClick={e => isClickable && handleFileClick(file, e)}
        sx={{
          px: 0,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          cursor: isClickable ? 'pointer' : 'default',
          pointerEvents: isClickable ? 'auto' : 'none',
          '&:hover': isClickable
            ? {
                backgroundColor: 'action.hover',
              }
            : {},
          '&:last-child': {
            borderBottom: 'none',
          },
        }}
      >
        <Box
          sx={{ mr: 2, display: 'flex', alignItems: 'center', flexShrink: 0 }}
        >
          {getFileIcon(file.name)}
        </Box>
        <Tooltip title={(file as any).description || file.name} arrow>
          <ListItemText
            primary={(file as any).description || file.name}
            sx={{
              flex: 1,
              minWidth: 0,
              pr: allowDeletion ? 1 : 0,
              '& .MuiListItemText-primary': {
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#333',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          />
        </Tooltip>
        {allowDeletion && (
          <IconButton
            onClick={e => {
              e.stopPropagation();
              handleRemoveFile(index);
            }}
            size="small"
            sx={{
              ml: 1,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: '#ffebee',
              },
            }}
          >
            <XIcon sx={{ fontSize: '16px', color: '#666' }} />
          </IconButton>
        )}
      </ListItem>
    );
  };

  const renderDialogContent = () => (
    <DialogContent sx={{ pb: 2 }}>
      <List sx={{ p: 0 }}>{selectedFiles.map(renderFileListItem)}</List>
    </DialogContent>
  );

  const renderDialogActions = () => (
    <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
      <Button
        variant="contained"
        startIcon={<AttachFileIcon />}
        onClick={handleFileBrowserClick}
        sx={{
          backgroundColor: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
        }}
      >
        Attach More
      </Button>
      <Button
        variant="outlined"
        onClick={() => setShowAllFiles(false)}
        sx={{
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          '&:hover': {
            borderColor: theme.palette.primary.dark,
            backgroundColor: theme.palette.primary.light + '20',
          },
        }}
      >
        Close
      </Button>
    </DialogActions>
  );

  const renderFileDialog = () => (
    <Dialog
      open={showAllFiles}
      onClose={() => setShowAllFiles(false)}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
          },
        },
      }}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
    >
      {renderDialogTitle()}
      {renderDialogContent()}
      {renderDialogActions()}
    </Dialog>
  );

  const renderMainContent = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        width: disableSpacer ? 'auto' : '100%',
        minHeight: '40px',
        overflow: 'hidden',
      }}
    >
      {showChips &&
        selectedFiles.length > 0 &&
        maxChipsVisible > 0 &&
        renderChipsSection()}
      {!disableSpacer &&
        (!showChips || selectedFiles.length === 0 || maxChipsVisible === 0) &&
        renderSpacer()}
      {renderAttachButton()}
      {renderHiddenFileInput()}
      {renderFileDialog()}
    </Box>
  );

  return renderMainContent();
}
